/**
 * Runtime config SEAM. app-core ships COMPILED dist (tsc), so it cannot read
 * `import.meta.env` (Vite only replaces env in the app it builds, not in a
 * pre-compiled package). Each shell app reads its OWN env and injects an
 * AppConfig via <AppConfigProvider>; app-core components read useAppConfig().
 *
 * Only API concerns live here (apiMode/apiBaseUrl). AUTH is NOT here — each app
 * plugs its own auth provider into the auth seam (mock/oidc/initData-exchange),
 * so "which auth" is an app decision, not an app-core config field.
 *
 * ⚠️ `RouteViewAnalytics.authMode` below is NOT an exception to that rule
 * (architect, review of `web!629`, on exactly this question). It is a build-time
 * DEPLOYMENT LABEL for ADR-141 telemetry — the same category as `collectorUrl`/
 * `websiteId` beside it — not the auth MECHANISM this docblock scopes. Nothing
 * here lets app-core decide which auth provider runs; each app still supplies
 * its own value.
 */
import { createContext, useContext } from 'react';
import { createElement } from 'react';
/**
 * Which DM family a surface writes into.
 *
 * `'telegram'` is the mini-app, which clause 1 designates the **`tg-` writer** —
 * not a viewer of it. That is the platform's shipped shape and not only ruling
 * text: the mini-app JWT is the only token carrying `tg_uid` (ADR-082 #9), which
 * exists precisely so its send leg can address the `tg-` DM.
 *
 * Written as a mapping from `Surface` rather than as an inline `=== 'telegram'`
 * test at each call site, so the day a sixth shell declares a new surface there
 * is exactly one place that has to answer "which family does it write into?".
 *
 * The `switch` + `const unreachable: never` is what makes the compiler ASK, and
 * it is the point of the function rather than a style preference. This shipped in
 * web!330 as `surface === 'telegram' ? 'TG' : 'APP'` under a docstring claiming
 * the closed union was enough — it is not: a ternary has a default arm, so a new
 * member type-checks and silently becomes `'APP'`. Measured, not reasoned (umnyi,
 * web!330 review): a sixth member produced ZERO errors against the ternary and
 * `TS2322: Type 'string' is not assignable to type 'never'` against this form.
 *
 * The failure that mattered is asymmetric — a new APP-family surface would have
 * been right by luck, while a new TG-family shell (a second Telegram-native
 * surface carrying `tg_uid`) would have become `'APP'`, turning the pair UI on for
 * it. That is precisely the defect web!330 existed to fix, reinstated for the next
 * surface. Do NOT collapse this back to a ternary.
 */
export function writeFamily(surface) {
    switch (surface) {
        case 'telegram':
            return 'TG';
        case 'web':
        case 'desktop':
            return 'APP';
        default: {
            const unreachable = surface;
            return unreachable;
        }
    }
}
const AppConfigContext = createContext(null);
export function AppConfigProvider({ config, children }) {
    return createElement(AppConfigContext.Provider, { value: config }, children);
}
export function useAppConfig() {
    const ctx = useContext(AppConfigContext);
    if (!ctx)
        throw new Error('useAppConfig outside <AppConfigProvider>');
    return ctx;
}
//# sourceMappingURL=config.js.map